import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useParams } from "@/lib/next-compat";
import { Panel } from "@/components/Panel";
import { useTenant } from "@/lib/tenant";

const STATUS: Record<string, { label: string; tone: string }> = {
  PENDING: { label: "Menunggu", tone: "warn" },
  ASSIGNED: { label: "Ditugaskan", tone: "info" },
  IN_PROGRESS: { label: "Dikerjakan", tone: "info" },
  SUBMITTED: { label: "Menunggu Konfirmasi", tone: "warn" },
  COMPLETED: { label: "Selesai", tone: "success" },
  REJECTED: { label: "Ditolak", tone: "danger" },
  CANCELLED: { label: "Dibatalkan", tone: "muted" },
};
const PRIO_LABEL: Record<string, string> = { low: "Rendah", normal: "Normal", high: "Tinggi", urgent: "Mendesak" };
const wib = (d?: string | null) => (d ? new Date(new Date(d).getTime() + 7 * 3600e3).toISOString().slice(0, 16).replace("T", " ") : "-");
const fmtSize = (b: number) => (b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`);

function Chip({ tone, children }: { tone: string; children: React.ReactNode }) {
  const bg: Record<string, string> = { success: "var(--status-success-bg)", danger: "var(--status-danger-bg)", warn: "var(--status-warn-bg)", info: "var(--color-surface-sunken)", muted: "var(--color-surface-sunken)" };
  const fg: Record<string, string> = { success: "var(--status-success)", danger: "var(--status-danger)", warn: "var(--status-warn)", info: "var(--color-text)", muted: "var(--color-text-muted)" };
  return <span className="avio-chip" data-shape="pill" style={{ background: bg[tone] ?? bg.muted, color: fg[tone] ?? fg.muted }}>{children}</span>;
}

interface Att { id: string; filename: string; mimeType: string; sizeBytes: number; url: string | null; }
interface Upd { id: string; kind: string; body: string; isDraft: boolean; authorName: string; createdAt: string; attachments: Att[]; }
interface Detail {
  id: string; title: string; description: string | null; location: string | null; targetDate: string | null;
  priority: string; status: string; rejectReason: string | null; requesterId: string; assigneeId: string | null;
  requesterName: string; assigneeName: string | null; createdAt: string; completedAt: string | null;
  updates: Upd[]; attachments: Att[];
}
interface Assignee { id: string; name: string; email: string; hasPhone: boolean; }

export default function LiputanDetail() {
  const { id } = useParams<{ id: string }>();
  const { can, user } = useTenant();
  const canManage = can("manage_liputan");
  const [d, setD] = useState<Detail | null>(null);
  const [assignees, setAssignees] = useState<Assignee[]>([]);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  // update composer (laporan standar — satu jenis catatan)
  const [updBody, setUpdBody] = useState("");

  async function load() {
    const r = await fetch(`/api/v1/liputan/${id}`);
    if (r.status === 401) { window.location.href = "/login"; return; }
    if (r.ok) setD(await r.json());
    else setMsg((await r.json().catch(() => ({}))).error ?? "gagal memuat");
  }
  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    if (!canManage) return;
    fetch("/api/v1/liputan/assignees").then((r) => r.ok ? r.json() : []).then(setAssignees).catch(() => {});
  }, [canManage]);

  const isRequester = () => !!user?.id && d?.requesterId === user.id;
  const isAssignee = () => !!user?.id && d?.assigneeId === user.id;

  async function patch(body: Record<string, unknown>, okMsg: string) {
    setBusy(true); setMsg("");
    try {
      const r = await fetch(`/api/v1/liputan/${id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error ?? "gagal");
      setMsg(okMsg); await load();
    } catch (e) { setMsg(e instanceof Error ? e.message : "gagal"); } finally { setBusy(false); }
  }

  async function addUpdate() {
    if (!updBody.trim()) { setMsg("Isi laporan wajib."); return; }
    setBusy(true); setMsg("");
    try {
      const r = await fetch(`/api/v1/liputan/${id}/updates`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ body: updBody, kind: "PROGRESS" }) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error ?? "gagal");
      setUpdBody(""); setMsg("Laporan ditambahkan."); await load();
    } catch (e) { setMsg(e instanceof Error ? e.message : "gagal"); } finally { setBusy(false); }
  }

  async function upload(file: File) {
    setBusy(true); setMsg("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await fetch(`/api/v1/liputan/${id}/attachments`, { method: "POST", body: fd });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error ?? "gagal");
      setMsg(`Lampiran “${j.filename}” terunggah.`); await load();
    } catch (e) { setMsg(e instanceof Error ? e.message : "gagal"); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  }

  if (!d) {
    return (
      <div className="avio-rise">
        <Link to="/a/liputan" className="avio-btn" data-variant="ghost" data-size="sm"><i className="fa-solid fa-chevron-left" /> Kembali</Link>
        <div style={{ padding: 24 }}>{msg ? <div className="avio-banner" data-tone="danger"><i className="fa-solid fa-triangle-exclamation avio-banner__icon" /><div>{msg}</div></div> : <div className="avio-skeleton" style={{ height: 12, width: 240 }} />}</div>
      </div>
    );
  }

  const st = STATUS[d.status] ?? { label: d.status, tone: "muted" };
  const canWork = isAssignee() || canManage;                                    // kerja lapangan
  const canAttach = isRequester() || isAssignee() || canManage;
  const locked = ["COMPLETED", "REJECTED", "CANCELLED"].includes(d.status);

  return (
    <div className="avio-rise">
      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-4)", flexWrap: "wrap" }}>
        <Link to="/a/liputan" className="avio-btn" data-variant="ghost" data-size="sm"><i className="fa-solid fa-chevron-left" /> Kembali</Link>
        <Chip tone={st.tone}>{st.label}</Chip>
      </div>

      <div style={{ marginTop: "var(--space-5)" }}>
        <h1 className="avio-page-title" style={{ margin: 0 }}>{d.title}</h1>
        <div className="avio-t-sm avio-muted" style={{ marginTop: 4, display: "flex", gap: "var(--space-6)", flexWrap: "wrap" }}>
          <span><i className="fa-solid fa-user" /> Pengaju: {d.requesterName}</span>
          {d.location && <span><i className="fa-solid fa-location-dot" /> {d.location}</span>}
          {d.targetDate && <span><i className="fa-solid fa-calendar" /> Target: {wib(d.targetDate).slice(0, 10)}</span>}
          <span><i className="fa-solid fa-flag" /> {PRIO_LABEL[d.priority] ?? d.priority}</span>
          <span><i className="fa-solid fa-user-pen" /> Petugas: {d.assigneeName ?? "belum ditugaskan"}</span>
        </div>
      </div>

      {msg && <div className="avio-banner" data-tone="info"><i className="fa-solid fa-circle-info avio-banner__icon" /><div>{msg}</div></div>}
      {d.status === "REJECTED" && d.rejectReason && <div className="avio-banner" data-tone="danger"><i className="fa-solid fa-ban avio-banner__icon" /><div>Alasan ditolak: {d.rejectReason}</div></div>}

      <div className="avio-grid">
        <div data-col="8">
         <div style={{ display: "grid", gap: "var(--space-6)" }}>
          {d.description && (
            <Panel icon="fa-solid fa-align-left" title="Deskripsi">
              <p style={{ whiteSpace: "pre-wrap", margin: 0 }}>{d.description}</p>
            </Panel>
          )}

          {/* Timeline laporan/progres */}
          <Panel icon="fa-solid fa-timeline" title="Laporan & Progres">
            {d.updates.length === 0 ? (
              <p className="avio-t-sm avio-muted" style={{ margin: 0 }}>Belum ada laporan.</p>
            ) : (
              <div style={{ display: "grid", gap: "var(--space-5)" }}>
                {d.updates.map((u) => (
                  <div key={u.id} style={{ borderLeft: "3px solid var(--color-border-strong)", paddingLeft: "var(--space-5)" }}>
                    <div style={{ display: "flex", gap: "var(--space-4)", alignItems: "center", flexWrap: "wrap" }}>
                      <Chip tone="info">Laporan</Chip>
                      <span className="avio-sub">{u.authorName} · {wib(u.createdAt)}</span>
                    </div>
                    <p style={{ whiteSpace: "pre-wrap", margin: "6px 0 0" }}>{u.body}</p>
                    {u.attachments.length > 0 && (
                      <div style={{ display: "flex", gap: "var(--space-4)", flexWrap: "wrap", marginTop: 6 }}>
                        {u.attachments.map((a) => (
                          <a key={a.id} href={a.url ?? "#"} target="_blank" rel="noreferrer" className="avio-chip" data-shape="pill">
                            <i className="fa-solid fa-paperclip" /> {a.filename} <span className="avio-muted">({fmtSize(a.sizeBytes)})</span>
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Composer: hanya petugas/koordinator, saat masih berjalan */}
            {canWork && !locked && (
              <div style={{ marginTop: "var(--space-6)", borderTop: "1px solid var(--color-border)", paddingTop: "var(--space-5)", display: "grid", gap: "var(--space-4)" }}>
                <textarea className="avio-input" rows={3} value={updBody} onChange={(e) => setUpdBody(e.target.value)} placeholder="Tulis catatan atau laporan peliputan…" />
                <div>
                  <button className="avio-btn" data-variant="primary" disabled={busy} onClick={addUpdate}><i className="fa-solid fa-plus" /> Tambah Laporan</button>
                </div>
              </div>
            )}
          </Panel>

          {/* Lampiran umum request */}
          <Panel icon="fa-solid fa-paperclip" title="Lampiran"
            action={canAttach && !locked ? (
              <>
                <input ref={fileRef} type="file" style={{ display: "none" }} onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
                <button className="avio-btn" data-variant="secondary" data-size="sm" disabled={busy} onClick={() => fileRef.current?.click()}>
                  <i className={`fa-solid ${busy ? "fa-circle-notch fa-spin" : "fa-upload"}`} /> Unggah
                </button>
              </>
            ) : undefined}>
            {d.attachments.length === 0 ? (
              <p className="avio-t-sm avio-muted" style={{ margin: 0 }}>Belum ada lampiran. Maks 25 MB per berkas.</p>
            ) : (
              <div style={{ display: "flex", gap: "var(--space-4)", flexWrap: "wrap" }}>
                {d.attachments.map((a) => (
                  <a key={a.id} href={a.url ?? "#"} target="_blank" rel="noreferrer" className="avio-chip" data-shape="pill">
                    <i className="fa-solid fa-file" /> {a.filename} <span className="avio-muted">({fmtSize(a.sizeBytes)})</span>
                  </a>
                ))}
              </div>
            )}
          </Panel>
         </div>
        </div>

        {/* Panel aksi status */}
        <div data-col="4">
         <div style={{ display: "grid", gap: "var(--space-6)" }}>
          <Panel icon="fa-solid fa-sliders" title="Aksi">
            <div style={{ display: "grid", gap: "var(--space-4)" }}>
              {/* Penugasan — koordinator */}
              {canManage && !locked && (
                <div style={{ display: "grid", gap: 6 }}>
                  <span className="avio-field__label">Tugaskan ke petugas</span>
                  <select className="avio-input" value={d.assigneeId ?? ""} disabled={busy}
                    onChange={(e) => patch({ assigneeId: e.target.value || null }, "Penugasan diperbarui.")}>
                    <option value="">— belum ditugaskan —</option>
                    {assignees.map((a) => (
                      <option key={a.id} value={a.id}>{a.name}{a.hasPhone ? "" : " (tanpa WA)"}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Transisi status kontekstual */}
              {canWork && d.status === "ASSIGNED" && (
                <button className="avio-btn" data-variant="primary" disabled={busy} onClick={() => patch({ status: "IN_PROGRESS" }, "Peliputan dimulai.")}><i className="fa-solid fa-play" /> Mulai Kerjakan</button>
              )}
              {canWork && d.status === "IN_PROGRESS" && (
                <button className="avio-btn" data-variant="primary" disabled={busy} onClick={() => patch({ status: "SUBMITTED" }, "Laporan diajukan untuk konfirmasi.")}><i className="fa-solid fa-flag-checkered" /> Ajukan Laporan Final</button>
              )}
              {(isRequester() || canManage) && d.status === "SUBMITTED" && (
                <>
                  <button className="avio-btn" data-variant="primary" disabled={busy} onClick={() => patch({ status: "COMPLETED" }, "Peliputan dikonfirmasi selesai.")}><i className="fa-solid fa-circle-check" /> Konfirmasi Selesai</button>
                  <button className="avio-btn" data-variant="secondary" data-size="sm" disabled={busy} onClick={() => patch({ status: "IN_PROGRESS" }, "Dikembalikan untuk revisi.")}><i className="fa-solid fa-rotate-left" /> Minta Revisi</button>
                </>
              )}
              {canManage && (d.status === "PENDING" || d.status === "ASSIGNED") && (
                <button className="avio-btn" data-variant="danger" data-size="sm" disabled={busy} onClick={() => {
                  const reason = prompt("Alasan penolakan?"); if (reason === null) return;
                  patch({ status: "REJECTED", rejectReason: reason }, "Permintaan ditolak.");
                }}><i className="fa-solid fa-ban" /> Tolak</button>
              )}
              {(isRequester() || canManage) && !locked && (
                <button className="avio-btn" data-variant="ghost" data-size="sm" disabled={busy} onClick={() => {
                  if (!confirm("Batalkan permintaan peliputan ini?")) return;
                  patch({ status: "CANCELLED" }, "Permintaan dibatalkan.");
                }}><i className="fa-solid fa-xmark" /> Batalkan</button>
              )}

              {locked && <p className="avio-t-sm avio-muted" style={{ margin: 0 }}>Permintaan sudah {st.label.toLowerCase()} — tidak ada aksi lanjut.</p>}
            </div>
          </Panel>

          <Panel icon="fa-solid fa-clock" title="Linimasa">
            <div className="avio-t-sm" style={{ display: "grid", gap: 4 }}>
              <div>Dibuat: <b>{wib(d.createdAt)}</b></div>
              {d.completedAt && <div>Selesai: <b>{wib(d.completedAt)}</b></div>}
            </div>
          </Panel>
         </div>
        </div>
      </div>
    </div>
  );
}
